"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.QuantityFlattener = void 0;
const echain_shared_ts_utils_1 = require("@igus/echain-shared-ts-utils");
const Quantity_1 = require("./Quantity");
const Q_1 = require("./Q");
const Acceleration_1 = require("./Acceleration");
const Angle_1 = require("./Angle");
const AnglePerLength_1 = require("./AnglePerLength");
const Area_1 = require("./Area");
const BendingFactor_1 = require("./BendingFactor");
const Force_1 = require("./Force");
const Frequency_1 = require("./Frequency");
const Length_1 = require("./Length");
const Temperature_1 = require("./Temperature");
const Velocity_1 = require("./Velocity");
const Volume_1 = require("./Volume");
const Weight_1 = require("./Weight");
const WeightPerLength_1 = require("./WeightPerLength");
/**
 * The `QuantityFlattener` parses a response data object
 * and replaces all quantity objects by literals.
 *
 * This is done by adding a postfix to the property key on base of the unit.
 *
 * Example:
 *
 *      BelastungFLG: [
 *          {
 *              FZ: QuantityJson (Length)
 *              FL: QuantityJson (Force)
 *          }
 *          ...
 *      ]
 *
 *      =>
 *
 *      BelastungFLG: [
 *          {
 *              FZ_mm: number
 *              FL_N: number
 *          }
 *          ...
 *      ]
 */
class QuantityFlattener {
    /**
     * Processes the transformation.
     */
    static process(data) {
        // Casting as T is nasty. In fact we should retrieve unknown
        // and let a typeguard check if it is really T.
        //
        // Since we potentially apply this on 30k records during a fetch from a client,
        // we leave it up to runtime errors being catched.
        return this.flattenRecursive(data);
    }
    /**
     * Processes the transformation of a data tree structure recursive.
     *
     * Example data to process:
     * [
     *  {
     *      id: 1
     *      laenge: {
     *          Unit: "m",
     *          Value: 3
     *      },
     *      description: {
     *          DE: "Leitung"
     *      },
     *      measurements: [{
     *          A:  {
     *              Unit: "m",
     *              Value: 3
     *          },
     *          B:  {
     *              Unit: "kg",
     *              Value: 77
     *          }
     *      }],
     *      shouldntbepossiblebutwhoknows: undefined|null
     *  }
     * ]
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static flattenRecursive(data) {
        // return array recursion
        if (Array.isArray(data)) {
            const items = [];
            for (const item of data) {
                items.push(this.flattenRecursive(item));
            }
            return items;
        }
        // check object
        else if (data != null && typeof data === 'object') {
            // return object recursion
            const obj = {};
            for (const key in data) {
                if (!Object.prototype.hasOwnProperty.call(data, key))
                    continue;
                const whatever = data[key];
                // transform quantity
                if ((0, Quantity_1.isQuantityJson)(whatever)) {
                    const { postfix, value } = this.flattenQuantity(whatever);
                    // set new property with a unit-based postfix
                    obj[key + '_' + postfix] = value;
                }
                else {
                    obj[key] = this.flattenRecursive(whatever);
                }
            }
            return obj;
        }
        // return any literals, null, undefined...
        return data;
    }
    static flattenQuantity(input) {
        const normUnit = {
            Acceleration: Acceleration_1.AccelerationUnits.m_s2,
            Angle: Angle_1.AngleUnits.deg,
            AnglePerLength: AnglePerLength_1.AnglePerLengthUnits.deg_m,
            Area: Area_1.AreaUnits.m2,
            BendingFactor: BendingFactor_1.BendingFactorUnits.xd,
            Force: Force_1.ForceUnits.N,
            Frequency: Frequency_1.FrequencyUnits.hz,
            Length: Length_1.LengthUnits.mm,
            Temperature: Temperature_1.TemperatureUnits.celsius,
            Velocity: Velocity_1.VelocityUnits.m_s,
            Volume: Volume_1.VolumeUnits.m_3,
            Weight: Weight_1.WeightUnits.g,
            WeightPerLength: WeightPerLength_1.WeightPerLengthUnits.kg_m,
        };
        const flatPostfix = {
            Acceleration: 'm_s2',
            Angle: 'deg',
            AnglePerLength: 'deg_m',
            Area: 'm2',
            BendingFactor: 'xd',
            Force: 'N',
            Frequency: 'hz',
            Length: 'mm',
            Temperature: 'C',
            Velocity: 'm_s',
            Volume: 'm_3',
            Weight: 'g',
            WeightPerLength: 'kg_m',
        };
        const q = (0, Q_1.Q)(input);
        const postfix = flatPostfix[q.type];
        const value = q.getValue(normUnit[q.type]);
        if (!postfix) {
            throw new Error(`${this.className}.flattenQuantity() No postfix found for quantity ${q.toString()}.`);
        }
        if (!echain_shared_ts_utils_1.NumberUtils.isDefinedNumber(value)) {
            throw new Error(`${this.className}.flattenQuantity() No value found for quantity ${q.toString()}.`);
        }
        return {
            postfix,
            value,
        };
    }
}
exports.QuantityFlattener = QuantityFlattener;
QuantityFlattener.className = 'QuantityFlattener';
//# sourceMappingURL=QuantityFlattener.js.map